import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Layout } from "../";
import {
  Loader,
  UpgradeContainer,
  GameContextSelector,
} from "../../components";
import { Container } from "./styled";

const Lineups = ({ appData }) => {
  const sessionUser = sessionStorage.getItem("user");
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [lineups, setLineups] = useState([]);
  const [sortedLineups, setSortedLineups] = useState([]);
  const [currentItems, setCurrentItems] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "player_number",
    direction: "asc",
  });
  const [gameContextData, setGameContextData] = useState({
    activeLeague: null,
    activePhase: null,
    activeSeason: null,
    activeTeam: null,
    activeRival: null,
    baseTeam: null,
    isH2HActive: null,
    games: null,
    statInX: null,
    statInY: null,
    isLoading: false,
  });

  // Estado para manejar la página actual y los elementos por página
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  // Manejar cambios del GameContextSelector
  const handleGameContextChange = useCallback((contextData) => {
    setGameContextData((prev) => ({
      ...prev,
      ...contextData,
    }));
  }, []);

  function calculatePlayedTime(entryTime, exitTime) {
    // Convierte un tiempo en formato "MM:SS" a segundos
    const timeToSeconds = (time) => {
      const [hours, minutes, seconds] = time.split(":").map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    };

    const entrySeconds = timeToSeconds(entryTime);
    const exitSeconds = timeToSeconds(exitTime);

    return Math.abs(exitSeconds - entrySeconds);
  }

  function convertSeconds(time) {
    // Calcular minutos enteros y segundos restantes
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;

    // Retornar en el formato solicitado
    return `${minutes}.${seconds}`;
  }

  // Función para comparar alineaciones
  function isEqual(arr1, arr2) {
    if (arr1?.length !== arr2?.length) return false;

    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();

    for (let i = 0; i < sortedArr1.length; i++) {
      if (sortedArr1[i] !== sortedArr2[i]) return false;
    }
    return true;
  }

  useEffect(() => {
    if (gameContextData.games !== null && gameContextData.games !== undefined) {
      const loadData = async () => {
        // Mostramos el loader
        setIsLoading(true);

        // Cede el control al navegador para renderizar el loader
        await new Promise((resolve) => setTimeout(resolve, 0));

        const lineups = [];
        const newLineup = new Map();
        const lastTime = new Map();

        gameContextData.games
          .filter(
            (game) =>
              gameContextData.activePhase === null ||
              game.stage === gameContextData.activePhase
          )
          .forEach((game) => {
            const gameId = game._id;

            const gamePeriods = Object.keys(game?.playByPlay[0])
              .filter(
                (k) => k.startsWith("PERIODO") || k === "INICIO DEL PARTIDO"
              )
              .sort((a, b) => {
                if (a === "INICIO DEL PARTIDO") return -1;
                if (b === "INICIO DEL PARTIDO") return 1;
                return (
                  Number.parseInt(a.replace("PERIODO ", "")) -
                  Number.parseInt(b.replace("PERIODO ", ""))
                );
              });

            gamePeriods.forEach((key) => {
              const playByPlayData = game.playByPlay[0];
              const groupedEventsByTime = groupEventsByTime(
                playByPlayData,
                key
              );

              Object.keys(groupedEventsByTime).forEach((time, i) => {
                processEventsAtSameTime(
                  groupedEventsByTime[time],
                  newLineup,
                  appData,
                  lineups,
                  gameId,
                  lastTime
                );
                if (
                  key !== "INICIO DEL PARTIDO" &&
                  i === Object.keys(groupedEventsByTime).length - 2
                ) {
                  Object.keys(newLineup).forEach((teamId) => {
                    if (newLineup[teamId].length === 5) {
                      const currentLineup = lineups.find((l) =>
                        isEqual(l.lineup, newLineup[teamId])
                      );
                      currentLineup.exitTime = "00:00:00";
                      let timePlayed = calculatePlayedTime(
                        currentLineup.time,
                        "00:00:00"
                      );
                      currentLineup.stats.MINS += timePlayed;
                    }
                  });
                  Object.keys(newLineup).forEach((teamId) => {
                    newLineup[teamId] = [];
                  });
                }
              });
            });
          });

        if (gameContextData.activeLeague !== null) {
          const modifiedLineups = lineups.map((lineupObj) => ({
            ...lineupObj,
            lineup: lineupObj.lineup.map((player, index) =>
              index === lineupObj.lineup.length - 1 ? player : player + " - "
            ),
          }));
          setLineups(modifiedLineups);
        }
        setIsLoading(false);
      };
      loadData();
    }
  }, [appData, gameContextData, gameContextData.games]);

  useEffect(() => {
    // Calcular los elementos que se mostrarán en la página actual
    const totalItems = sortedLineups?.length;
    setTotalPages(Math.ceil(totalItems / itemsPerPage));
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setCurrentItems(
      sortedLineups.length > 0
        ? sortedLineups?.slice(indexOfFirstItem, indexOfLastItem)
        : null
    );
  }, [sortedLineups, currentPage]);

  // Effect for sorting lineups based on sortConfig, activeTeam, etc.
  useEffect(() => {
    if (lineups.length > 0) {
      setIsTableLoading(true);

      const filteredAndSorted = lineups
        .filter(
          (l) =>
            !gameContextData.activeTeam || l.team === gameContextData.activeTeam
        )
        .sort((a, b) => {
          const valueA = a.stats[sortConfig.key];
          const valueB = b.stats[sortConfig.key];
          return sortConfig.direction === "asc"
            ? valueA - valueB
            : valueB - valueA;
        });

      setSortedLineups(filteredAndSorted); // Update only derived sorted lineups
      setIsTableLoading(false);
    } else {
      setSortedLineups([]); // Update only derived sorted lineups
    }
  }, [
    lineups,
    sortConfig.key,
    sortConfig.direction,
    gameContextData.activeTeam,
  ]);

  function groupEventsByTime(events, key) {
    return events[key]?.reduce((acc, event) => {
      acc[event.time] = acc[event.time] || [];
      acc[event.time].push(event);
      return acc;
    }, {});
  }

  // Función para encontrar el equipo de un jugador
  function findPlayerTeam(player, appData, activeSeason) {
    const indexOfPlayer = appData.players.findIndex((object) => {
      return (
        `${object.player_lastname
          ?.toUpperCase()
          .replace(/\s+/g, "")},${object.player_name
          ?.toUpperCase()
          .replace(/\s+/g, "")}` === player?.toUpperCase().replace(/\s+/g, "")
      );
    });

    if (indexOfPlayer > -1) {
      const teamInfo = appData.players[indexOfPlayer].teams_history.find(
        (item) => item.season === activeSeason
      );
      return teamInfo ? teamInfo.teamId : null;
    }
    return null;
  }

  // Función para manejar eventos de entrada a pista
  function handlePlayerEntry(
    gameAction,
    teamId,
    newLineup,
    lineups,
    appData,
    gameId,
    lastTime
  ) {
    if (!newLineup[teamId]) newLineup[teamId] = [];
    newLineup[teamId].push(gameAction.player);

    if (newLineup[teamId].length === 5) {
      const existingLineup = lineups.find((l) =>
        isEqual(l.lineup, newLineup[teamId])
      );

      if (!existingLineup) {
        lineups.push({
          time: gameAction.time,
          exitTime: null,
          lineup: [...newLineup[teamId].sort()],
          gameIndexes: [gameId],
          team: teamId,
          stats: initializeStats(),
        });
      } else {
        updateExistingLineup(existingLineup, gameId, gameAction.time);
      }
      lastTime[teamId] = gameAction.time;
    }
  }

  // Función para inicializar las estadísticas
  function initializeStats() {
    return {
      PJ: 1,
      MINS: 0,
      PTS: 0,
      "2PM": 0,
      "2PA": 0,
      "2P%": 0,
      "3PM": 0,
      "3PA": 0,
      "3P%": 0,
      FTM: 0,
      FTA: 0,
      "FT%": 0,
      ORB: 0,
      DRB: 0,
      REB: 0,
      ASI: 0,
      TUR: 0,
      ROB: 0,
      TAP: 0,
      TAPR: 0,
      FUL: 0,
      FULR: 0,
    };
  }

  // Función para actualizar alineación existente
  function updateExistingLineup(existingLineup, gameId, time) {
    if (!existingLineup.gameIndexes.includes(gameId)) {
      existingLineup.gameIndexes.push(gameId);
      existingLineup.stats.PJ += 1;
    }
    existingLineup.time = time;
  }

  // Función para manejar eventos de anotación
  function handleScoringAction(action, currentLineup) {
    if (!currentLineup) return;

    if (action.includes("TIRO LIBRE ANOTADO")) {
      currentLineup.stats.PTS += 1;
      currentLineup.stats.FTM += 1;
      currentLineup.stats.FTA += 1;
      currentLineup.stats["FT%"] =
        currentLineup.stats.FTM / currentLineup.stats.FTA;
    } else if (action.includes("TIRO LIBRE FALLADO")) {
      currentLineup.stats.FTA += 1;
      currentLineup.stats["FT%"] =
        currentLineup.stats.FTM / currentLineup.stats.FTA;
    } else if (action.includes("CANASTA DE 2 PUNTOS")) {
      currentLineup.stats.PTS += 2;
      currentLineup.stats["2PM"] += 1;
      currentLineup.stats["2PA"] += 1;
      currentLineup.stats["2P%"] =
        currentLineup.stats["2PM"] / currentLineup.stats["2PA"];
    } else if (action.includes("TIRO DE 2 FALLADO")) {
      currentLineup.stats["2PA"] += 1;
      currentLineup.stats["2P%"] =
        currentLineup.stats["2PM"] / currentLineup.stats["2PA"];
    } else if (action.includes("TRIPLE")) {
      currentLineup.stats.PTS += 3;
      currentLineup.stats["3PM"] += 1;
      currentLineup.stats["3PA"] += 1;
      currentLineup.stats["3P%"] =
        currentLineup.stats["3PM"] / currentLineup.stats["3PA"];
    } else if (action.includes("TIRO DE 3 FALLADO")) {
      currentLineup.stats["3PA"] += 1;
      currentLineup.stats["3P%"] =
        currentLineup.stats["3PM"] / currentLineup.stats["3PA"];
    } else if (action.includes("REBOTE DEFENSIVO")) {
      currentLineup.stats.DRB += 1;
      currentLineup.stats.REB += 1;
    } else if (action.includes("REBOTE OFENSIVO")) {
      currentLineup.stats.ORB += 1;
      currentLineup.stats.REB += 1;
    } else if (action.includes("ASISTENCIA")) {
      currentLineup.stats.ASI += 1;
    } else if (action.includes("PÉRDIDA DE BALÓN")) {
      currentLineup.stats.TUR += 1;
    } else if (action.includes("RECUPERACIÓN")) {
      currentLineup.stats.ROB += 1;
    } else if (action.includes("TAPÓN REALIZADO")) {
      currentLineup.stats.TAP += 1;
    } else if (action.includes("TAPÓN RECIBIDO")) {
      currentLineup.stats.TAPR += 1;
    } else if (action.includes("FALTA COMETIDA")) {
      currentLineup.stats.FUL += 1;
    } else if (action.includes("FALTA RECIBIDA")) {
      currentLineup.stats.FULR += 1;
    }
  }

  // Función para manejar eventos de salida de pista
  function handlePlayerExit(gameAction, teamId, newLineup, lineups, lastTime) {
    const currentLineup = lineups.find((l) =>
      isEqual(l.lineup, newLineup[teamId])
    );
    if (newLineup[teamId]?.length === 5 && currentLineup) {
      let timePlayed = calculatePlayedTime(currentLineup.time, gameAction.time);
      currentLineup.stats.MINS += timePlayed;
      currentLineup.exitTime = gameAction.time;
      lastTime[teamId] = gameAction.time;
    }
    newLineup[teamId] = newLineup[teamId]?.filter(
      (p) => p !== gameAction.player
    );
  }

  const lineupMap = new Map();

  function processEventsAtSameTime(
    eventsAtTime,
    newLineup,
    appData,
    lineups,
    gameId,
    lastTime
  ) {
    eventsAtTime.forEach((gameAction) => {
      const teamId = findPlayerTeam(
        gameAction.player,
        appData,
        gameContextData.activeSeason
      );
      if (!teamId) return;

      const lineupKey = newLineup[teamId]?.sort().join("-");
      let currentLineup = lineupMap.get(lineupKey);

      if (gameAction.action.includes("ABANDONA LA PISTA")) {
        handlePlayerExit(gameAction, teamId, newLineup, lineups, lastTime);
      } else if (gameAction.action.includes("ENTRA A PISTA")) {
        handlePlayerEntry(
          gameAction,
          teamId,
          newLineup,
          lineups,
          appData,
          gameId,
          lastTime
        );
      } else {
        const currentLineup = lineups.find((l) =>
          isEqual(l.lineup, newLineup[teamId]?.sort())
        );
        handleScoringAction(gameAction.action, currentLineup);
      }

      // Actualiza el mapa para alineaciones actuales
      if (newLineup[teamId]?.length === 5) {
        lineupMap.set(newLineup[teamId].sort().join("-"), currentLineup);
      }
    });
  }

  const handleSort = (key) => {
    setIsTableLoading(true);
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Función para cambiar de página
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <>
      {isLoading && <Loader />}

      <Layout isPositionUnset={true}>
        {sessionUser !== undefined &&
        sessionUser !== null &&
        sessionUser?.plan !== "Basic" ? (
          <Container>
            <GameContextSelector
              appData={appData}
              typeOfSelector="lineups"
              onChange={handleGameContextChange}
            />
            {currentItems !== null && currentItems.length > 0 ? (
              <>
                <ul className="pagination">
                  <select
                    onChange={(e) =>
                      handlePageChange(Number(e.target.value) + 1)
                    }
                    value={currentPage - 1}
                  >
                    {[...Array(totalPages)].map((_, i) => (
                      <option
                        key={i}
                        value={i}
                        className={`page-item ${
                          currentPage === i + 1 ? "active" : ""
                        }`}
                      >
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  <li className="total-pages-number">de {totalPages}</li>
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link left"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </button>
                  </li>
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link right"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                    </button>
                  </li>
                </ul>
                <div className="table-scrollable">
                  <table
                    id="table"
                    cellPadding="10"
                    className={isTableLoading ? "loading" : null}
                  >
                    <thead>
                      <tr>
                        <th className="lineup-title">Lineup</th>
                        <th>Equipo</th>
                        <th
                          className={
                            sortConfig.key === "PJ" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "PJ" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("PJ")}
                        >
                          PJ
                        </th>
                        <th
                          className={
                            sortConfig.key === "MINS" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "MINS" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("MINS")}
                        >
                          MINS
                        </th>
                        <th
                          className={
                            sortConfig.key === "PTS" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "PTS" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("PTS")}
                        >
                          PTS
                        </th>
                        <th
                          className={
                            sortConfig.key === "2PM" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "2PM" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("2PM")}
                        >
                          2PA
                        </th>
                        <th
                          className={
                            sortConfig.key === "2PA" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "2PA" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("2PA")}
                        >
                          2PI
                        </th>
                        <th
                          className={
                            sortConfig.key === "2P%" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "2P%" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("2P%")}
                        >
                          2P%
                        </th>
                        <th
                          className={
                            sortConfig.key === "3PM" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "3PM" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("3PM")}
                        >
                          3PA
                        </th>
                        <th
                          className={
                            sortConfig.key === "3PA" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "3PA" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("3PA")}
                        >
                          3PI
                        </th>
                        <th
                          className={
                            sortConfig.key === "3P%" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "3P%" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("3P%")}
                        >
                          3P%
                        </th>
                        <th
                          className={
                            sortConfig.key === "FTM" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "FTM" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("FTM")}
                        >
                          TLA
                        </th>
                        <th
                          className={
                            sortConfig.key === "FTA" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "FTA" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("FTA")}
                        >
                          TLI
                        </th>
                        <th
                          className={
                            sortConfig.key === "FT%" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "FT%" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("FT%")}
                        >
                          TL%
                        </th>
                        <th
                          className={
                            sortConfig.key === "ORB" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "ORB" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("ORB")}
                        >
                          REBO
                        </th>
                        <th
                          className={
                            sortConfig.key === "DRB" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "DRB" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("DRB")}
                        >
                          REBD
                        </th>
                        <th
                          className={
                            sortConfig.key === "REB" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "REB" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("REB")}
                        >
                          REB
                        </th>
                        <th
                          className={
                            sortConfig.key === "ASI" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "ASI" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("ASI")}
                        >
                          ASI
                        </th>
                        <th
                          className={
                            sortConfig.key === "TUR" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "TUR" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("TUR")}
                        >
                          PER
                        </th>
                        <th
                          className={
                            sortConfig.key === "ROB" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "ROB" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("ROB")}
                        >
                          REC
                        </th>
                        <th
                          className={
                            sortConfig.key === "TAP" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "TAP" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("TAP")}
                        >
                          TAPC
                        </th>
                        <th
                          className={
                            sortConfig.key === "TAPR" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "TAPR" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("TAPR")}
                        >
                          TAPR
                        </th>
                        <th
                          className={
                            sortConfig.key === "FUL" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "FUL" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("FUL")}
                        >
                          FALC
                        </th>
                        <th
                          className={
                            sortConfig.key === "FULR" &&
                            sortConfig.direction === "asc"
                              ? "up-arrow"
                              : sortConfig.key === "FULR" &&
                                sortConfig.direction === "desc"
                              ? "down-arrow"
                              : null
                          }
                          onClick={() => handleSort("FULR")}
                        >
                          FALR
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems?.map((item, index) => (
                        <tr key={`${item.teamId}-${index}`}>
                          <td className="lineup">{item.lineup}</td>
                          <td>
                            {appData?.teams
                              ?.filter((p) => p._id === item.team)[0]
                              .name.slice(0, 3)}
                          </td>
                          <td>{item.stats.PJ}</td>
                          <td>{convertSeconds(item.stats.MINS)}</td>
                          <td>{item.stats.PTS}</td>
                          <td>{item.stats["2PM"]}</td>
                          <td>{item.stats["2PA"]}</td>
                          <td>
                            {(isNaN(item.stats["2P%"])
                              ? 0
                              : item.stats["2P%"] * 100
                            ).toFixed(2)}
                          </td>
                          <td>{item.stats["3PM"]}</td>
                          <td>{item.stats["3PA"]}</td>
                          <td>
                            {(isNaN(item.stats["3P%"])
                              ? 0
                              : item.stats["3P%"] * 100
                            ).toFixed(2)}
                          </td>
                          <td>{item.stats.FTM}</td>
                          <td>{item.stats.FTA}</td>
                          <td>
                            {(isNaN(item.stats["FT%"])
                              ? 0
                              : item.stats["FT%"] * 100
                            ).toFixed(2)}
                          </td>
                          <td>{item.stats.ORB}</td>
                          <td>{item.stats.DRB}</td>
                          <td>{item.stats.REB}</td>
                          <td>{item.stats.ASI}</td>
                          <td>{item.stats.TUR}</td>
                          <td>{item.stats.ROB}</td>
                          <td>{item.stats.TAP}</td>
                          <td>{item.stats.TAPR}</td>
                          <td>{item.stats.FUL}</td>
                          <td>{item.stats.FULR}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : currentItems === null &&
              gameContextData.activeLeague !== null ? (
              <div
                className="flex_center_center"
                style={{ textAlign: "center", width: "100%" }}
              >
                <h1>No hay información</h1>
              </div>
            ) : null}
          </Container>
        ) : sessionUser && sessionUser?.plan === "Basic" ? (
          <UpgradeContainer>
            <h2 style={{ marginBottom: "0" }}>
              Herramienta solo para usuarios Premium
            </h2>
            <p>Actualiza tu susctipción ahora.</p>
            <div className="btns-container">
              <a className="btn-primary" href="/app/suscriptions">
                Ver suscripciones
              </a>
            </div>
          </UpgradeContainer>
        ) : isLoading ? (
          <Loader />
        ) : (
          <UpgradeContainer />
        )}
      </Layout>
    </>
  );
};

export default Lineups;
